/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The pre-migration readiness report (POST /endpoints/{source}/actions {"readiness"}): an overall
 * verdict, the link rate the estimates use (editable), and one row per VM with its verdict, its
 * checks (pass / warn / fail, in the order Swift sends them), how long its syncs take and the
 * target size Swift recommends. Used by the wizard's Readiness step and the transfer page.
 */

import * as React from "react";
import styled, { css } from "styled-components";
import {
  ChevronDown,
  CircleCheck,
  CircleX,
  Cpu,
  Gauge,
  HardDrive,
  LoaderCircle,
  Network,
  RefreshCw,
  Server,
  Timer,
  TriangleAlert,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { ActionButton } from "@src/components/ui/DetailsKit";
import { Pill, T9, formatDateTime } from "@src/components/ui/Lists/ListTable";
import type { PillTone } from "@src/components/ui/Lists/ListTable";
import { LinkButton, Notice, OpsInput } from "@src/components/ui/OpsDialog";
import { formatBytes } from "@src/utils/TransferProgress";

import type {
  ReadinessCheck,
  ReadinessCheckStatus,
  ReadinessInstance,
  ReadinessReport as Report,
  ReadinessVerdict,
} from "@src/@types/Cutover";

export const VERDICT: { [v in ReadinessVerdict]: { label: string; tone: PillTone; icon: LucideIcon } } = {
  ready: { label: "Ready", tone: "success", icon: CircleCheck },
  ready_with_warnings: { label: "Ready with warnings", tone: "warning", icon: TriangleAlert },
  blocked: { label: "Blocked", tone: "danger", icon: CircleX },
};
const CHECK: { [s in ReadinessCheckStatus]: { icon: LucideIcon; colour: string; label: string } } = {
  pass: { icon: CircleCheck, colour: "var(--t9-success-700)", label: "Passed" },
  warn: { icon: TriangleAlert, colour: "var(--t9-warning-700)", label: "Warning" },
  fail: { icon: CircleX, colour: "var(--t9-danger-700)", label: "Failed" },
};

const LINK_SOURCE: { [s: string]: string } = {
  explicit: "the rate you entered",
  history: "median of past syncs between these endpoints",
  default: "SwiftIQ's default (no sync history yet)",
};

export const worstVerdict = (instances: ReadinessInstance[]): ReadinessVerdict => {
  if (instances.some(i => i.verdict === "blocked")) return "blocked";
  if (instances.some(i => i.verdict === "ready_with_warnings")) return "ready_with_warnings";
  return "ready";
};

/** The report without some VMs (removed from the wizard's selection): counts and verdict recomputed. */
export const withoutInstances = (report: Report, remove: string[]): Report => {
  const instances = report.instances.filter(i => !remove.includes(i.instance));
  return {
    ...report,
    instances,
    verdict: worstVerdict(instances),
    counts: {
      ready: instances.filter(i => i.verdict === "ready").length,
      ready_with_warnings: instances.filter(i => i.verdict === "ready_with_warnings").length,
      blocked: instances.filter(i => i.verdict === "blocked").length,
    },
    total_bytes: instances.reduce((s, i) => s + (i.estimates?.bytes || 0), 0),
  };
};

// ------------------------------------------------------------------ styles

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;
const BANNER: { [v in ReadinessVerdict]: { bg: string; border: string; fg: string } } = {
  ready: { bg: "var(--t9-selected-bg)", border: "var(--t9-brand-100)", fg: "var(--t9-primary)" },
  ready_with_warnings: { bg: "var(--t9-info-50)", border: "var(--t9-accent-500)", fg: "var(--t9-info-700)" },
  blocked: { bg: "var(--t9-danger-50)", border: "var(--t9-danger-200)", fg: "var(--t9-danger-700)" },
};
const Banner = styled.div<{ verdict: ReadinessVerdict }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid ${props => BANNER[props.verdict].border};
  background: ${props => BANNER[props.verdict].bg};
  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--t9-bg-surface);
    color: ${props => BANNER[props.verdict].fg};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .text {
    flex: 1;
    min-width: 0;
  }
  .title {
    font-size: 16px;
    line-height: 22px;
    font-weight: 600;
    color: ${props => BANNER[props.verdict].fg};
  }
  .sub {
    font-size: 13px;
    line-height: 18px;
    color: ${T9.slate600};
  }
`;
const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const Tile = styled.div`
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  padding: 12px 14px;
  background: var(--t9-bg-surface);
  min-width: 0;
  .l {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${T9.slate500};
  }
  .v {
    margin-top: 4px;
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
    color: ${T9.slate900};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .d {
    margin-top: 2px;
    font-size: 12px;
    line-height: 16px;
    color: ${T9.slate500};
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
const LinkEdit = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  input {
    width: 90px;
    height: 28px;
  }
  span {
    font-size: 12px;
    color: ${T9.slate500};
  }
`;
const Table = styled.div`
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  overflow: hidden;
  background: var(--t9-bg-surface);
`;
const GRID = "minmax(180px, 1.6fr) 150px 100px 92px 92px minmax(130px, 1fr) 20px";
const HeadRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID};
  column-gap: 14px;
  align-items: center;
  height: 36px;
  padding: 0 12px 0 16px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${T9.slate500};
  white-space: nowrap;
`;
const VmRow = styled.div<{ open?: boolean; blocked?: boolean }>`
  display: grid;
  grid-template-columns: ${GRID};
  column-gap: 14px;
  align-items: center;
  min-height: 52px;
  padding: 6px 12px 6px 16px;
  border-bottom: 1px solid ${T9.slate100};
  cursor: pointer;
  background: ${props => (props.open ? T9.slate50 : "var(--t9-bg-surface)")};
  ${props =>
    props.blocked
      ? css`
          box-shadow: inset 3px 0 0 var(--t9-danger-700);
        `
      : ""}
  &:hover {
    background: ${T9.slate50};
  }
`;
const VmName = styled.div`
  min-width: 0;
  .n {
    font-size: 14px;
    font-weight: 500;
    color: ${T9.slate900};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .o {
    font-size: 12px;
    color: ${T9.slate500};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const Counts = styled.div`
  display: flex;
  gap: 10px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  span {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
`;
const Num = styled.div`
  font-size: 13px;
  color: ${T9.slate700};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Chevron = styled(ChevronDown)<{ open?: boolean }>`
  color: ${T9.slate400};
  transform: rotate(${props => (props.open ? 180 : 0)}deg);
  transition: transform 150ms;
`;
const Expanded = styled.div`
  padding: 6px 16px 16px 16px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(240px, 1fr);
  gap: 16px;
  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Checks = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
`;
const CheckItem = styled.li<{ status: ReadinessCheckStatus }>`
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid ${T9.slate100};
  &:last-child {
    border-bottom: 0;
  }
  > svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${props => CHECK[props.status].colour};
  }
  .body {
    flex: 1;
    min-width: 0;
  }
  .t {
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
    color: ${T9.slate900};
  }
  .d {
    font-size: 12px;
    line-height: 17px;
    color: ${T9.slate600};
    margin-top: 1px;
    overflow-wrap: anywhere;
  }
  .fix {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
    font-size: 12px;
    color: ${props => (props.status === "fail" ? "var(--t9-danger-700)" : "var(--t9-info-700)")};
  }
`;
const SideCard = styled.div`
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: start;
  .h {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: ${T9.slate500};
  }
  .kv {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    color: ${T9.slate700};
    b {
      font-weight: 600;
      color: ${T9.slate900};
      font-variant-numeric: tabular-nums;
      text-align: right;
    }
  }
  .note {
    font-size: 12px;
    line-height: 16px;
    color: ${T9.slate500};
  }
`;
const SmallButton = styled(ActionButton)`
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 28px 20px;
  border: 1px dashed ${T9.slate300};
  border-radius: 8px;
  color: ${T9.slate600};
  font-size: 14px;
  .spin {
    animation: rd-spin 1s linear infinite;
    color: ${T9.brand500};
  }
  @keyframes rd-spin {
    to {
      transform: rotate(360deg);
    }
  }
  .s {
    font-size: 12px;
    color: ${T9.slate500};
  }
`;

// ------------------------------------------------------------------ helpers

const countChecks = (checks: ReadinessCheck[]) => ({
  pass: checks.filter(c => c.status === "pass").length,
  warn: checks.filter(c => c.status === "warn").length,
  fail: checks.filter(c => c.status === "fail").length,
});

const issuesLine = (vm: ReadinessInstance) => {
  const issues = vm.checks.filter(c => c.status !== "pass");
  if (!issues.length) return null;
  return issues
    .slice(0, 2)
    .map(c => c.title)
    .join(", ")
    .concat(issues.length > 2 ? ` +${issues.length - 2}` : "");
};

const sizeLine = (vm: ReadinessInstance) => {
  const r = vm.recommended;
  if (!r) return null;
  const shape = [r.vcpus ? `${r.vcpus} vCPU` : null, r.memory_mb ? `${Math.round(r.memory_mb / 1024)} GiB` : null].filter(Boolean).join(", ");
  return { main: r.flavor || shape || "Source size", sub: r.flavor ? shape : "sized freely on the target" };
};

export type FixState = { [key: string]: "applied" | undefined };
export const fixKey = (vm: string, check: ReadinessCheck) => `${vm}:${check.id}:${check.fix?.option || ""}`;

type Props = {
  report: Report | null;
  loading: boolean;
  error?: string | null;
  linkMbps: string;
  onLinkMbpsChange: (value: string) => void;
  onRefresh: () => void;
  /** Apply a check's fix to the wizard's options (the wizard only). */
  onFix?: (vm: ReadinessInstance, check: ReadinessCheck) => void;
  /** Runs a fix that changes the source platform (check.fix.action); actionBusy is its fixKey while it runs. */
  onAction?: (vm: ReadinessInstance, check: ReadinessCheck) => void;
  actionBusy?: string | null;
  fixes?: FixState;
  /** Remove a VM from the wizard's selection (the wizard only). */
  onRemove?: (vm: ReadinessInstance) => void;
  /** Where network mappings are made ("the Networks step", "Edit transfer"). */
  networksHint?: string;
  instanceCount?: number;
};

const ReadinessReportView = (props: Props) => {
  const { report } = props;
  const [open, setOpen] = React.useState<{ [k: string]: boolean }>({});

  React.useEffect(() => {
    // Open the VMs that need attention on the first report.
    if (report && !Object.keys(open).length) {
      const next: { [k: string]: boolean } = {};
      report.instances.forEach(i => {
        if (i.verdict === "blocked") next[i.instance] = true;
      });
      if (!Object.keys(next).length && report.instances.length === 1) next[report.instances[0].instance] = true;
      setOpen(next);
    }
  }, [report]);

  if (props.loading && !report) {
    return (
      <Loading data-test-id="readiness-loading">
        <LoaderCircle size={24} className="spin" />
        <div>
          <div>Checking {props.instanceCount ? `${props.instanceCount} VM${props.instanceCount === 1 ? "" : "s"}` : "the VMs"} against the target…</div>
          <div className="s">SwiftIQ reads each VM from the source in parallel; this takes up to about 45 seconds. Nothing is changed.</div>
        </div>
      </Loading>
    );
  }
  if (props.error && !report) {
    return (
      <Notice
        tone="danger"
        title="The readiness check did not run"
        actions={
          <ActionButton type="button" variant="secondary" onClick={props.onRefresh}>
            <RefreshCw size={14} />
            Try again
          </ActionButton>
        }
      >
        {props.error}
      </Notice>
    );
  }
  if (!report) return null;

  const verdict = VERDICT[report.verdict];
  const counts = report.counts || {};
  const VerdictIcon = verdict.icon;
  const link = report.link;
  const titles: { [v in ReadinessVerdict]: string } = {
    ready: "Ready to migrate",
    ready_with_warnings: "Ready, with warnings to review",
    blocked: `${counts.blocked || report.instances.filter(i => i.verdict === "blocked").length} VM${
      (counts.blocked || 0) === 1 ? "" : "s"
    } can't migrate yet`,
  };

  return (
    <Wrapper data-test-id="readinessReport">
      <Banner verdict={report.verdict}>
        <span className="icon">
          <VerdictIcon size={22} />
        </span>
        <div className="text">
          <div className="title">{titles[report.verdict]}</div>
          <div className="sub">
            {[
              counts.ready ? `${counts.ready} ready` : null,
              counts.ready_with_warnings ? `${counts.ready_with_warnings} with warnings` : null,
              counts.blocked ? `${counts.blocked} blocked` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            {report.target?.title ? ` · target ${report.target.title}` : ""}
            {report.generated_at ? ` · checked ${formatDateTime(report.generated_at)}` : ""}
            {report.cached ? " (cached)" : ""}
          </div>
        </div>
        <ActionButton type="button" variant="secondary" onClick={props.onRefresh} disabled={props.loading} data-test-id="readiness-refresh">
          {props.loading ? <LoaderCircle size={14} className="spin" /> : <RefreshCw size={14} />}
          {props.loading ? "Checking…" : "Refresh"}
        </ActionButton>
      </Banner>

      <Tiles>
        <Tile>
          <div className="l">
            <Timer size={13} />
            First sync, all VMs
          </div>
          <div className="v">{report.full_sync_text_all || "—"}</div>
          <div className="d">They share the link, so this is the time for all of them together.</div>
        </Tile>
        <Tile>
          <div className="l">
            <HardDrive size={13} />
            Data to copy
          </div>
          <div className="v">{report.total_bytes ? formatBytes(report.total_bytes) : "—"}</div>
          <div className="d">Provisioned disk size; the first sync reads only allocated blocks where the source tracks them.</div>
        </Tile>
        <Tile>
          <div className="l">
            <Gauge size={13} />
            Link rate
          </div>
          <div className="v">{link ? `${Math.round(link.mbps)} Mbit/s` : "—"}</div>
          <div className="d" title={link?.detail}>
            {link ? link.detail || LINK_SOURCE[link.source] || link.source : ""}
            {link?.limited_by_bandwidth_limit_mibps ? ` · capped by the ${link.limited_by_bandwidth_limit_mibps} MiB/s limit` : ""}
          </div>
          <LinkEdit>
            <OpsInput
              type="number"
              min={1}
              placeholder="Mbit/s"
              value={props.linkMbps}
              onChange={e => props.onLinkMbpsChange(e.target.value)}
              aria-label="Link rate in Mbit/s"
              data-test-id="readiness-link"
            />
            <span>Mbit/s</span>
            <LinkButton type="button" onClick={props.onRefresh} disabled={props.loading}>
              Use
            </LinkButton>
          </LinkEdit>
        </Tile>
        <Tile>
          <div className="l">
            <Server size={13} />
            Target
          </div>
          <div className="v">{report.target?.title || report.target?.platform || "—"}</div>
          <div className="d">
            {report.instances.length} VM{report.instances.length === 1 ? "" : "s"} checked
            {report.cache_seconds ? ` · results kept for ${Math.round(report.cache_seconds / 60)} min` : ""}
          </div>
        </Tile>
      </Tiles>

      {props.error ? <Notice tone="danger">{props.error}</Notice> : null}

      <Table>
        <HeadRow>
          <span>VM</span>
          <span>Verdict</span>
          <span>Checks</span>
          <span>First sync</span>
          <span>Daily sync</span>
          <span>Recommended size</span>
          <span />
        </HeadRow>
        {report.instances.map(vm => {
          const isOpen = Boolean(open[vm.instance]);
          const v = VERDICT[vm.verdict] || VERDICT.blocked;
          const c = countChecks(vm.checks || []);
          const size = sizeLine(vm);
          const issues = issuesLine(vm);
          return (
            <React.Fragment key={vm.instance}>
              <VmRow
                open={isOpen}
                blocked={vm.verdict === "blocked"}
                onClick={() => setOpen({ ...open, [vm.instance]: !isOpen })}
                data-test-id="readiness-vm"
              >
                <VmName>
                  <div className="n" title={vm.instance}>
                    {vm.name || vm.instance}
                  </div>
                  <div className="o">{issues || vm.os_name || vm.os_type || "Operating system not reported"}</div>
                </VmName>
                <div>
                  <Pill tone={v.tone} icon={v.icon}>
                    {v.label}
                  </Pill>
                </div>
                <Counts>
                  <span style={{ color: "var(--t9-success-700)" }} title="Passed">
                    <CircleCheck size={12} />
                    {c.pass}
                  </span>
                  <span style={{ color: c.warn ? "var(--t9-warning-700)" : T9.slate400 }} title="Warnings">
                    <TriangleAlert size={12} />
                    {c.warn}
                  </span>
                  <span style={{ color: c.fail ? "var(--t9-danger-700)" : T9.slate400 }} title="Failed">
                    <CircleX size={12} />
                    {c.fail}
                  </span>
                </Counts>
                <Num title={vm.estimates?.note || undefined}>{vm.estimates?.full_sync_text || "—"}</Num>
                <Num title={vm.estimates?.incremental_basis || undefined}>
                  {vm.estimates?.incremental_sync_text || <span style={{ color: T9.slate400 }}>unknown</span>}
                </Num>
                <VmName>
                  <div className="n" style={{ fontSize: 13 }}>
                    {size ? size.main : "—"}
                  </div>
                  {size?.sub ? <div className="o">{size.sub}</div> : null}
                </VmName>
                <Chevron size={16} open={isOpen} />
              </VmRow>
              {isOpen ? (
                <Expanded>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                    <Checks>
                      {(vm.checks || []).map(check => {
                        const S = CHECK[check.status] || CHECK.warn;
                        const Icon = S.icon;
                        const key = fixKey(vm.instance, check);
                        const applied = props.fixes?.[key] === "applied";
                        return (
                          <CheckItem key={`${check.id}-${check.title}`} status={check.status} data-test-id={`readiness-check-${check.status}`}>
                            <Icon size={16} aria-label={S.label} />
                            <div className="body">
                              <div className="t">{check.title}</div>
                              {check.detail ? <div className="d">{check.detail}</div> : null}
                              {check.fix && check.status !== "pass" ? (
                                <div className="fix">
                                  <Wrench size={12} />
                                  <span>{check.fix.text}</span>
                                  {check.fix.action && props.onAction ? (
                                    <SmallButton
                                      type="button"
                                      variant="primary"
                                      disabled={props.actionBusy === key}
                                      onClick={e => {
                                        e.stopPropagation();
                                        props.onAction!(vm, check);
                                      }}
                                      data-test-id="readiness-action"
                                    >
                                      {props.actionBusy === key ? "Applying…" : check.fix.action.label}
                                    </SmallButton>
                                  ) : null}
                                  {check.fix.option && props.onFix ? (
                                    applied ? (
                                      <Pill tone="success" icon={CircleCheck}>
                                        Set
                                      </Pill>
                                    ) : (
                                      <SmallButton
                                        type="button"
                                        variant="secondary"
                                        onClick={e => {
                                          e.stopPropagation();
                                          props.onFix!(vm, check);
                                        }}
                                        data-test-id="readiness-fix"
                                      >
                                        {check.fix.option_label || "Fix: set it"}
                                      </SmallButton>
                                    )
                                  ) : null}
                                  {check.fix.networks?.length ? (
                                    <span style={{ color: T9.slate600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                      <Network size={12} />
                                      {check.fix.networks.join(", ")}: map {check.fix.networks.length === 1 ? "it" : "them"} {props.networksHint || "on the Networks step"}.
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </CheckItem>
                        );
                      })}
                    </Checks>
                    {vm.verdict === "blocked" && props.onRemove ? (
                      <Notice
                        tone="danger"
                        actions={
                          <ActionButton
                            type="button"
                            variant="danger"
                            onClick={e => {
                              e.stopPropagation();
                              props.onRemove!(vm);
                            }}
                            data-test-id="readiness-remove"
                          >
                            Remove from this transfer
                          </ActionButton>
                        }
                      >
                        This VM can't be migrated until the failed checks are fixed at the source or the target.
                      </Notice>
                    ) : null}
                  </div>
                  <SideCard>
                    <div className="h">Estimates</div>
                    <div className="kv">
                      First sync <b>{vm.estimates?.full_sync_text || "—"}</b>
                    </div>
                    <div className="kv">
                      A day of changes <b>{vm.estimates?.incremental_sync_text || "unknown"}</b>
                    </div>
                    <div className="kv">
                      Data <b>{vm.estimates?.bytes ? formatBytes(vm.estimates.bytes) : "—"}</b>
                    </div>
                    {vm.estimates?.change_bytes_per_day ? (
                      <div className="kv">
                        Changes per day <b>{formatBytes(vm.estimates.change_bytes_per_day)}</b>
                      </div>
                    ) : null}
                    <div className="note">
                      {[vm.estimates?.note, vm.estimates?.incremental_basis].filter(Boolean).join(". ")}
                    </div>
                    <div className="h" style={{ marginTop: 4 }}>
                      Recommended size
                    </div>
                    <div className="kv">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Cpu size={13} />
                        {size?.main || "—"}
                      </span>
                      <b>{size?.sub || ""}</b>
                    </div>
                    {vm.recommended?.disks_gib?.length ? (
                      <div className="kv">
                        Disks <b>{vm.recommended.disks_gib.map(g => `${g} GiB`).join(" + ")}</b>
                      </div>
                    ) : null}
                    <div className="note">
                      {[vm.recommended?.basis, vm.recommended?.note].filter(Boolean).join(". ")}
                    </div>
                  </SideCard>
                </Expanded>
              ) : null}
            </React.Fragment>
          );
        })}
      </Table>
    </Wrapper>
  );
};

export default ReadinessReportView;
